// Per-payment-link resolution for the APPLE_PAY_API_ENABLED flag: the native
// flow can be rolled out to a single link in prod (via the flag's `testers`
// list) while hidden for everyone else.

// Raw shape of the flag's Firestore doc (only the fields we care about).
export interface ApplePayFlagData {
  environments?: Record<string, boolean> | null;
  allowForEveryone?: Record<string, boolean> | null;
  // From Firestore — elements are not guaranteed to be strings at runtime.
  testers?: unknown[] | null;
}

export interface ApplePayFlagState {
  // Feature is active in this environment (prod well-known switched to Modak).
  cutover: boolean;
  // Native Apple Pay is active for THIS payment link (everyone, or a tester link).
  enabled: boolean;
}

export const resolveApplePayFlagState = (
  data: ApplePayFlagData | null | undefined,
  env: string,
  paymentLinkId?: string,
): ApplePayFlagState => {
  const cutover = data?.environments?.[env] === true;
  const forEveryone = data?.allowForEveryone?.[env] === true;
  const testersRaw = data?.testers;
  const testers = Array.isArray(testersRaw) ? testersRaw : [];
  // The feature-flag admin only accepts email-format entries, so a payment link
  // id is stored as `<paymentLinkId>@modak.live`; match the part before the `@`.
  // testers come from Firestore, so guard against non-string elements.
  const isTester =
    !!paymentLinkId &&
    testers.some(
      tester =>
        typeof tester === 'string' && tester.split('@')[0] === paymentLinkId,
    );

  return {cutover, enabled: cutover && (forEveryone || isTester)};
};

// Native is the only Apple Pay path now: show it when this link is enabled and
// the device supports it, otherwise hide the option entirely (either flag lever
// off → hidden, users fall back to card). The old Web Components Apple Pay is
// dead — prod serves Modak's well-known, so its merchant validation would fail.
export const resolveApplePayVisible = (
  enabled: boolean,
  isNativeAvailable: boolean,
): boolean => enabled && isNativeAvailable;
